<template>
  <form class="create-account-form" @submit.prevent="submit">
    <div v-if="!noFirstNameLastname" class="create-account-form-top flex">
      <div class="w-full mr-5">
        <OInput
          :id="getFieldId('firstName')"
          v-model="formData.firstName"
          type="text"
          :label="$t('SignUp.Form.FirstName')"
          :input-classes="['w-full', 'mb-3', 'o-body']"
          :error="getErrorMessage('firstName') || errors.firstName"
          :required="true"
          auto-complete="given-name"
          :exclude-symbols="defaultExcludeSymbols"
          @input="onChange($event, 'firstName')"
        />
      </div>
      <div class="w-full">
        <OInput
          :id="getFieldId('lastName')"
          v-model="formData.lastName"
          type="text"
          :label="$t('SignUp.Form.LastName')"
          :input-classes="['w-full', 'mb-3', 'o-body']"
          :error="getErrorMessage('lastName') || errors.lastName"
          :required="true"
          auto-complete="family-name"
          :exclude-symbols="defaultExcludeSymbols"
          @input="onChange($event, 'lastName')"
        />
      </div>
    </div>
    <span v-if="isGermany" class="o-caption block mb-5" v-html="$t('SignUp.Form.NameNotice')" />
    <div v-if="showPhoneNumberInput" class="create-account-form-top flex">
      <div class="w-full">
        <OInput
          :id="getFieldId('phoneNumber')"
          v-model="formData.phoneNumber"
          tag="tel-input"
          :country="country"
          :label="$t('Member.PhoneNumber')"
          :input-classes="['w-full', 'mb-2', 'o-body']"
          :error="getErrorMessage('phoneNumber') || errors.phoneNumber"
          :required="true"
          @input="onChange($event, 'phoneNumber')"
        />
      </div>
    </div>
    <span v-if="showPhoneNumberInput" class="o-caption block mb-5 neutral600" v-html="$t('SignUp.Form.PhoneNumberNotice')" />
    <div v-if="!minimal || noFirstNameLastname" class="create-account-form-bottom">
      <OInput
        :id="getFieldId('email')"
        v-model="formData.email"
        type="email"
        :label="$t('SignUp.Form.Email')"
        :input-classes="['w-full', 'mb-3', 'o-body']"
        :error="getErrorMessage('email') || errors.email"
        :required="true"
        auto-complete="email"
        :exclude-symbols="defaultExcludeSymbols"
        @input="onChange($event, 'email')"
      />
      <OInput
        :id="getFieldId('password')"
        v-model="formData.password"
        :placeholder="placeholder"
        type="password"
        :label="$t('SignUp.Form.Password')"
        :input-classes="['w-full', 'mb-3', 'o-body']"
        :error="getErrorMessage('password') || errors.password"
        :required="!mockPassword"
        :title="$t('SignUp.Summary.Form.Min5')"
        auto-complete="new-password"
        class="password-input"
        @input="onChange($event, 'password')"
      />
      <div class="create-account-form-button mt-5 hidden lg:block">
        <OButton
          type="submit"
          :attribute="{ name: 'data-qa', value: 'signup-confirm-account' }"
          :variant="ButtonVariants.secondary"
        >
          {{ $t('SignUp.Proceed') }}
        </OButton>
      </div>
    </div>
  </form>
</template>

<script>
import { ButtonVariants } from '@/enums/components'
import { required, minLength, maxLength, email } from 'vuelidate/lib/validators'
import { CountryCodes, DEFAULT_INPUT_EXCLUDE_SYMBOLS } from '@/enums/general'
const OInput = () => import('@/components/OInput')
const OButton = () => import('@/components/OButton')

export default {
  name: 'CreateAccountForm',
  components: {
    OInput,
    OButton
  },
  props: {
    firstName: {
      type: String,
      default: ''
    },
    lastName: {
      type: String,
      default: ''
    },
    phoneNumber: {
      type: String,
      default: ''
    },
    showPhoneNumberInput: {
      type: Boolean,
      default: false
    },
    email: {
      type: String,
      default: ''
    },
    noFirstNameLastname: {
      type: Boolean,
      default: false
    },
    password: {
      type: String,
      default: ''
    },
    mockPassword: {
      type: Boolean,
      default: false
    },
    minimal: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    country: {
      type: String,
      default: CountryCodes.netherlands
    },
    customIds: {
      type: Object,
      default: () => ({})
    }
  },
  validations () {
    const validations = {
      ...(!this.noFirstNameLastname ? {
        firstName: {
          required,
          minLength: minLength(2),
          maxLength: maxLength(20)
        },
        lastName: {
          required,
          minLength: minLength(2),
          maxLength: maxLength(35)
        }
      } : {}),
      ...(this.minimal && !this.noFirstNameLastname ? {} : {
        email: {
          required,
          email
        },
        password: {
          required: this.mockPassword ? false : required,
          minLength: minLength(6),
          maxLength: maxLength(25)
        }
      }),
      ...(this.showPhoneNumberInput
        ? {
            phoneNumber: {
              required
            }
          } : {}
      )
    }

    return { formData: validations }
  },
  data () {
    return {
      formData: {
        firstName: this.firstName,
        lastName: this.lastName,
        phoneNumber: this.phoneNumber,
        email: this.email,
        password: this.password
      },
      invalidForm: false,
      ButtonVariants,
      CountryCodes
    }
  },
  computed: {
    placeholder () {
      return this.mockPassword ? '•••••••••••' : ''
    },
    isGermany () {
      return this.country === CountryCodes.germany
    }
  },
  watch: {
    firstName (newFirstName) {
      if (this.minimal && newFirstName) {
        this.formData.firstName = newFirstName
      }
    },
    lastName (newLastName) {
      if (this.minimal && newLastName) {
        this.formData.lastName = newLastName
      }
    },
    phoneNumber (newPhoneNumber) {
      if (this.showPhoneNumberInput && newPhoneNumber) {
        this.formData.phoneNumber = newPhoneNumber
      }
    }
  },
  created () {
    this.defaultExcludeSymbols = DEFAULT_INPUT_EXCLUDE_SYMBOLS
  },
  methods: {
    submit () {
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.invalidForm = true

        return
      }

      this.$emit('submit', this.formData)
    },
    onChange ($event, field) {
      this.$emit('change', { value: $event, field })
    },
    getErrorMessage (fieldName) {
      const item = this.$v.formData[fieldName]

      if (!item.$invalid || !this.invalidForm) {
        return ''
      }

      let errorMessage = this.$t('Error.invalid', { label: this.$t(`Form.Address.${fieldName}`) })

      const validations = {
        required: this.$t('Form.Error.Required'),
        email: this.$t('Form.Error.EmailIncorrect'),
        maxLength: this.$t('Form.Error.MaxLength', { value: item.$params.maxLength?.max }),
        minLength: this.$t('Form.Error.MinLength', { amount: item.$params.minLength?.min })
      }

      Object.keys(validations).forEach((rule) => {
        if (typeof item[rule] !== 'undefined' && !item[rule]) {
          errorMessage = validations[rule]
        }
      })

      return errorMessage
    },
    getFieldId (field) {
      return this.customIds[field] || field
    }
  }
}
</script>

<style lang="postcss" scoped>
>>> #password::placeholder {
  @apply text-gray-600;
}
</style>
